import React from 'react';
import PropTypes from 'prop-types';
import { Map as LeafletMap, TileLayer, CircleMarker, Marker, Polyline, Tooltip, Popup } from 'react-leaflet';
import { Spin, Divider, Icon, Popover } from 'antd';
import { inject, observer } from 'mobx-react';
import { toJS } from 'mobx';
import { Rnd } from 'react-rnd';
import L from 'leaflet';
import 'leaflet-easyprint';

const tLogo = require('./../images/tubitak_logo.svg');

global.Highcharts = require('highcharts');
require('highcharts/modules/exporting')(global.Highcharts);
require('highcharts/modules/offline-exporting')(global.Highcharts);
require('highcharts/modules/export-data')(global.Highcharts);
const ReactHighcharts = require('react-highcharts');

const Faults = props => {
  const { faultsInfo, faultNames, filter } = props;
  if (!faultsInfo.length) {
    return <span />;
  }
  const data = toJS(faultsInfo);

  // lime color if selected

  const faults = data.map(({ path, name, descriptiveName }) =>
    <Polyline positions={path} color={filter && faultNames.includes(name) ? "lime" : "blue"} key={name}>
      {filter && faultNames.includes(name) ?
        (<Tooltip permanent>{name}</Tooltip>) :
        (<Popup>{name}</Popup>)
      }
    </Polyline>
  );

  return faults;
};

Faults.propTypes = {
  faultsInfo: PropTypes.object.isRequired,
  faultNames: PropTypes.object.isRequired,
  filter: PropTypes.bool.isRequired
};

const Lines = props => {
  const poiLat = props.poi[0];
  const poiLng = props.poi[1];
  const { curveData, poiName, lineLimits } = props;
  if (!curveData.All) return null;

  const exportMenuItems = global.Highcharts.getOptions().exporting.buttons.contextButton.menuItems;
  exportMenuItems.splice(9, 3); // Remove options to display in div
  const colors = [
    '#1f77b4', // blue
    '#ff7f0e', // orange
    '#2ca02c', // green
    '#d62728', // red
    '#9467bd', // purple
    '#8c564b', // brown
    '#e377c2', // pink
    '#7f7f7f', // gray
  ];

  const series = Object.keys(curveData).map(curveName => {
    const defaults = {
      name: curveName,
      data: curveData[curveName].rows,
      color: colors.pop(),
    };

    if (curveName === 'All') {
      return {
        ...defaults,
        marker: {
          radius: 6,
        },
        lineWidth: 5,
      };
    }
    return defaults;
  });


  const config = {
    title: {
      text: 'Tsunami Hazard Curves',
      style: { fontSize: 18 },
    },
    subtitle: {
      text: `Hazard curves generated for POI ${poiName} located at Lat:${parseFloat(poiLat).toFixed(6)} Lng:${parseFloat(poiLng).toFixed(6)}`,
      style: { fontSize: 16 },
    },
    chart: {
      height: `${(9 / 16) * 100}%`, // 16:9 ratio
    },
    credits: {
      enabled: false,
      href: 'http://www.i4.com.tr',
      text: 'Made with ♥ by i4',
    },
    tooltip: {
      formatter() {
        return `Values for datapoint on <b>${this.series.name}</b><br/>MTA: <b>${this.x
          }m</b><br/>PoE: <b>${this.y.toPrecision(4)}</b>`;
      },
      style: {
        fontSize: 15,
      }
    },
    exporting: {
      filename: `hazard_curve_for_${poiLat}-${poiLng}`,
    },
    legend: {
      itemStyle: {
        fontSize: 15
      }
    },
    xAxis: {
      type: 'logarithmic',
      max: lineLimits[2] || 5,
      min: lineLimits[0] || 0.01,
      tickInterval: 0.2,
      startOnTick: true,
      endOnTick: true,
      gridLineWidth: 1,
      labels: {
        style: {
          fontSize: 15
        }
      },
      title: {
        enabled: true,
        text: 'Maximum Tsunami Amplitude(m) at 20m water depth',
        style: {
          fontStyle: 'italic',
          fontSize: 16
        },
      },
    },
    // 'Dash' | 'DashDot' | 'Dot' | 'LongDash' | 'LongDashDot' | 'LongDashDotDot' | 'ShortDash' | 'ShortDashDot' | 'ShortDashDotDot' | 'ShortDot' | 'Solid'
    yAxis: [
      {
        type: 'logarithmic',
        max: lineLimits[3] || 0.5,
        // min: lineLimits[1] || 0.000005,
        min: 0.0001,
        labels: {
          style: {
            fontSize: 15
          }
        },
        title: {
          enabled: true,
          text: 'Annual Probability of Exceedance',
          style: {
            fontStyle: 'italic',
            fontSize: 16
          },
        },
        plotLines: [
          {
            color: 'black', // Color value
            value: 1 / 475, // Start of the plot band
            width: 1, // End of the plot band
            label: {
              text: '475 yr return period',
              align: 'right',
              y: -5,
              style: {
                fontSize: 15
              }
            },
            dashStyle: 'LongDash'
          },
          {
            color: 'black', // Color value
            value: 1 / 2475, // Start of the plot band
            width: 1, // End of the plot band
            label: {
              text: '2475 yr return period',
              align: 'right',
              y: -5,
              style: {
                fontSize: 15
              }
            },
            dashStyle: 'LongDashDot'
          },
          {
            color: 'black', // Color value
            value: 1 / 4975, // Start of the plot band
            width: 1, // End of the plot band
            label: {
              text: '4975 yr return period',
              align: 'right',
              y: -5,
              style: {
                fontSize: 15
              }
            },
            dashStyle: 'ShortDash'
          }
        ],
      },
    ],
    series,
  };

  return <ReactHighcharts config={config} />;
};

Lines.propTypes = {
  poi: PropTypes.object.isRequired,
  curveData: PropTypes.object.isRequired,
  poiName: PropTypes.string.isRequired,
  lineLimits: PropTypes.object.isRequired,
};

const Circles = props => {
  const circleInfo = props.circleData.slice();
  if (circleInfo.length !== 0) {
    const circles = circleInfo[0].map((color, index) => (
      <CircleMarker
        center={[circleInfo[1][index][0], circleInfo[1][index][1]]}
        key={index}
        radius="5"
        opacity="1"
        fillOpacity="0.5"
        stroke
        color={color}
      />
    ));
    return circles;
  }
  return <span />;
};

Circles.propTypes = {
  circleData: PropTypes.object.isRequired,
};

const curveInfoContent = (
  <div>
    - This window is <b>resizable</b> and <b>draggable</b>.<br />- You can <b>filter</b> the curves clicking them on
    legend.
    <br />- <b>Export</b> options are under the top-right dropdown.
  </div>
);

@inject('dataStore')
@observer
class Map extends React.Component {
  static propTypes = {
    dataStore: PropTypes.object.isRequired,
  };
  constructor() {
    super();
    this.state = {
      lat: 37,
      lng: 17,
      zoom: 5,
    };
  }

  componentDidMount() {
    const { map } = this._map.contextValue;
    const { dataStore } = this.props;

    L.easyPrint({
      title: 'Print',
      position: 'topleft',
      sizeModes: ['Current', 'A4Portrait', 'A4Landscape'],
      filename: `${dataStore.currentMaptype}-${dataStore.currentMihPoe}`,
    }).addTo(map);

    L.easyPrint({
      title: 'Export as PNG',
      position: 'topleft',
      sizeModes: ['Current', 'A4Portrait', 'A4Landscape'],
      exportOnly: 'true',
      filename: `${dataStore.currentMaptype}-${dataStore.currentMihPoe}`,
    }).addTo(map);

    const logo = L.control({ position: 'bottomleft' });

    logo.onAdd = function () {
      const div = document.getElementsByClassName('map-credit')[0];
      return div;
    };
    logo.addTo(map);
  }

  onZoomEnd = e => {
    this.setState({
      zoom: e.target._zoom,
    });
  };

  getClickedLoc = e => {
    this.props.dataStore.getNearestPoi([e.latlng.lat, e.latlng.lng]).then(result => {
      this.props.dataStore.setCurveData(result.content);
    });
  };

  render() {
    const position = [this.state.lat, this.state.lng];
    const opacityDecreaseStyle = { opacity: '0.3' };

    return (
      <div className="map__wrapper">
        <Spin
          spinning={this.props.dataStore.loadingMap}
          tip="Loading..."
          style={{ zIndex: 1000, position: 'absolute' }}
        />
        <LeafletMap
          ref={c => (this._map = c)}
          id="map"
          minZoom={4}
          maxZoom={10}
          doubleClickZoom={false}
          maxBounds={[
            [-6.5, -54],
            [78.9, 116.7],
          ]}
          onZoomEnd={this.onZoomEnd}
          style={this.props.dataStore.loadingMap ? opacityDecreaseStyle : null}
          center={
            this.props.dataStore.markerLoc
              ? [this.props.dataStore.markerLoc[0], this.props.dataStore.markerLoc[1]]
              : position
          }
          onClick={this.getClickedLoc}
          zoom={this.state.zoom}
        >
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; National Geographic, Esri, DeLorme, NAVTEQ, UNEP-WCMC, USGS, NASA, ESA, METI, NRCAN, GEBCO, NOAA, iPC'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}"
          />

          <Circles circleData={this.props.dataStore.circlesInfo} />
          <Faults faultsInfo={this.props.dataStore.faultsInfo} faultNames={this.props.dataStore.top5FaultNames} filter={!!this.props.dataStore.isModalActive} />
          {this.props.dataStore.markerLoc && this.props.dataStore.isModalActive ? (
            <Marker
              position={[this.props.dataStore.markerLoc[0], this.props.dataStore.markerLoc[1]]}
              title="Active Point of Interest"
              draggable={false}
            />
          ) : null}
        </LeafletMap>
        <div className="map-credit">
          {
            /*
            <img
              style={{ height: 50, opacity: 0.8 }}
              alt="map credit"
              src={tLogo}
            />
            */
          }
        </div>
        <div className="draggable-legend">
          <Rnd
            default={{
              x: -5,
              y: -278,
              width: 140,
            }}
            enableResizing={false}
            bounds="body"
          >
            <div className="map-legend">
              <div className="map-legend__legendpalette" />
              <div className="map-legend__legendtext">
                {this.props.dataStore.currentMaptype === 'hazard' ? (
                  <div>
                    <div style={{ position: 'absolute' }}>0.05m</div>
                    <div style={{ position: 'absolute', top: Math.floor(242 * ((Math.log(0.1) - Math.log(0.05)) / (Math.log(1) - Math.log(0.05)))) }}>0.1m</div>
                    <div style={{ position: 'absolute', top: Math.floor(242 * ((Math.log(0.2) - Math.log(0.05)) / (Math.log(1) - Math.log(0.05)))) }}>0.2m</div>
                    <div style={{ position: 'absolute', top: Math.floor(242 * ((Math.log(0.5) - Math.log(0.05)) / (Math.log(1) - Math.log(0.05)))) }}>0.5m</div>
                    <div style={{ position: 'absolute', top: 242 }}>1m</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ position: 'absolute' }}>0.0005</div>
                    <div style={{ position: 'absolute', top: Math.floor(242 * ((Math.log(0.005) - Math.log(0.0005)) / (Math.log(0.1) - Math.log(0.0005)))) }}>0.005</div>
                    <div style={{ position: 'absolute', top: Math.floor(242 * ((Math.log(0.01) - Math.log(0.0005)) / (Math.log(0.1) - Math.log(0.0005)))) }}>0.01</div>
                    <div style={{ position: 'absolute', top: Math.floor(242 * ((Math.log(0.03) - Math.log(0.0005)) / (Math.log(0.1) - Math.log(0.0005)))) }}>0.03</div>
                    <div style={{ position: 'absolute', top: Math.floor(242 * ((Math.log(0.05) - Math.log(0.0005)) / (Math.log(0.1) - Math.log(0.0005)))) }}>0.05</div>
                    <div style={{ position: 'absolute', top: 242 }}>0.1</div>
                  </div>
                )}
              </div>
            </div>
          </Rnd>
        </div>
        {this.props.dataStore.markerLoc && this.props.dataStore.isModalActive ? (
          <Rnd
            default={{
              x: 40,
              y: 200,
              width: 750,
            }}
            minHeight={177}
            bounds="parent"
            lockAspectRatio
            dragHandleClassName="chart__drag-handler"
            enableResizing={{
              top: false,
              right: true,
              bottom: true,
              left: true,
              topRight: true,
              bottomRight: true,
              bottomLeft: true,
              topLeft: true,
            }}
            style={{
              zIndex: 1000,
              position: 'absolute',
              backgroundColor: 'white',
              borderRadius: '5px',
              paddingRight: '20px',
              boxShadow: '0px 0px 20px -1px rgba(0,0,0,0.67)',
            }}
            onResize={() => {
              this.forceUpdate();
            }}
          >
            <div className="chart__drag-handler">
              <div style={{ cursor: 'default' }}>
                <Popover style={{ backgroundColor: 'black' }} content={curveInfoContent}>
                  <Icon style={{ fontSize: 16 }} type="info-circle" />
                </Popover>
              </div>
              <div>
                <Icon style={{ fontSize: 20 }} type="ellipsis" />
              </div>
              <div onClick={() => this.props.dataStore.closeCurveModal()} style={{ cursor: 'default' }}>
                <Icon style={{ fontSize: 20 }} type="close" />
              </div>
            </div>
            <Divider />
            <div className="chart__wrapper">
              <Lines
                poi={this.props.dataStore.markerLoc}
                curveData={this.props.dataStore.curveData}
                poiName={this.props.dataStore.closestPoi}
                lineLimits={this.props.dataStore.lineLimits}
              />
            </div>
          </Rnd>
        ) : null}
      </div>
    );
  }
}

export default Map;



// WEBPACK FOOTER //
// ./src/components/Map.js